<template>
  <CDataTable
      :items-per-page="7"
      sorter
      pagination
      hover
      clickable-rows
      striped
      :items="filtered"
      :fields="fields"
      @row-clicked="onSelectRow"
      class="custom-table custom-table--uploader"
  >
    <template #no-items-view>
      <div class="text-center">
        <h6 class="py-3" v-translate>No files</h6>
      </div>
    </template>
    <template #sorting-icon="{ state, classes }">
      <div :class="classes">
        <CIcon name="cipCaretUp" size="lg" v-if="state === 'asc' || state === 0"></CIcon>
        <CIcon name="cipCaretUp" size="lg" v-else></CIcon>
      </div>
    </template>
    <template #actions-header>
      <CInput v-model="filter" size="sm" placeholder="Filter" class="m-0"/>
    </template>
    <template #md5-header>
      <CInputCheckbox custom @click="updateSelectStatus" :id="checkAllId"/>
    </template>
    <template #md5="{item}">
      <td>
        <CInputCheckbox custom :checked="selected[item.md5]" @update:checked="onSelectRow(item)"/>
      </td>
    </template>
    <template #name="{item}">
      <td>
        <Preview :file="item.file" icon-size="compact" @loadImage="$emit('loadImage',$event)"/>
        <CProgress v-if="item.file.inProgress > 0"
                   class="progress-xs p-uploader__progress"
                   :precision="1"
                   :value="item.file.progress"
        />
      </td>
    </template>
    <template #tags="{item}">
      <td>
        <CBadge class="badge-outline mr-2" shape="pill" color="primary" v-for="tag in item.tags" :key="tag">
          {{ tag }}
        </CBadge>

        <CBadge v-if="item.picaCode" class="badge-outline" shape="pill" color="success">
          PICA CODE: {{ item.picaCode }}
        </CBadge>
        <CBadge v-if="item.gallery" class="badge-outline" shape="pill" color="success">
          GALLERY: {{ item.gallery.name }}
        </CBadge>
        <CBadge v-if="item.disableAutotag" class="badge-outline" shape="pill" color="success">
          Autotag Disabled
        </CBadge>
        <CBadge v-if="item.free" class="badge-outline" shape="pill" color="success">
          Free
        </CBadge>
      </td>
    </template>
    <template #actions="{item}">
      <td>
        <CButtonClose class="list-item-closer mr-2" @click.stop="onDelete(item.file)">
          <CIcon name="cipClose" size="custom-size" width="15"/>
        </CButtonClose>
      </td>
    </template>
    <template #under-table>
      <slot name="footer"></slot>
    </template>
  </CDataTable>

</template>

<script>
import Preview from "@/domain/uploader/components/Preview.vue";
import Vue from "vue";

export default {
  name: "FileTable",
  components: {Preview},
  props: {
    rows: {
      type: Array,
      required: true
    },
    in_progress: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      selectAll: false,
      selected: {},
      filter: '',
      checkAllId: 'checkbox_' + Math.random().toString(36).slice(2, 9), // using random id because refs are not working with indeterminate
      elCheckAll: null
    }
  },

  watch: {
    selectedCount(newVal, oldVal) {
      this.updateSelectAllFlag(newVal)
    },
    filtered() {
      this.updateSelectAllFlag(this.selectedCount)
      this.emitSelected()
    }
  },

  mounted() {
    this.elCheckAll = document.getElementById(this.checkAllId)
    this.emitSelected()
  },

  computed: {
    files() {
      return this.rows.map((file) => {
        return {
          md5: file.md5,
          name: file.name,
          size: file.size,
          tags: file.tags,
          public: file.public,
          free: file.free,
          disableAutotag: file.disableAutotag,
          picaCode: file.picaCode,
          gallery: file.gallery,
          resolution: file.resolution || '',
          motivation: file.motivation,
          human_size: file.human_size,
          file: file,
        }
      })
    },
    filtered() {
      if (!this.filter) return this.files
      return this.files.filter((file) => {
        if (file.picaCode && file.picaCode.toLowerCase().includes(this.filter.toLowerCase())) return true
        if (file.name.toLowerCase().includes(this.filter.toLowerCase())) return true
        if (file.tags.join(' ').toLowerCase().includes(this.filter.toLowerCase())) return true
        return false
      })
    },
    fields() {
      return [
        {
          key: 'md5',
          label: this.$pgettext('uploader.to_upload', 'Select'),
          _style: 'width:50px;',
          sorter: false,
        },
        {key: 'name', label: this.$pgettext('uploader.to_upload', 'Name')},
        {key: 'tags', label: this.$pgettext('uploader.to_upload', 'Tags')},
        {key: 'human_size', label: this.$pgettext('uploader.to_upload', 'Size')},
        {key: 'resolution', label: this.$pgettext('uploader.to_upload', 'Resolution'),},
        {key: 'actions', label: '', sorter: false, _style: 'width:170px;',},
      ]
    },
    selectedCount() {
      return Object.keys(this.selected).length
    }
  },
  emits: ['delete', 'selected', 'retry', 'loadImage'],
  methods: {
    onDelete(file) {
      this.confirm({
        title: this.$gettext('Delete file'),
        message: this.$gettext('Are you sure you want to delete this file?'),
        cb_confirm: async () => {
          this.$emit('delete', [file])
        }
      })
    },
    updateSelectStatus() {
      if (this.selectedCount === 0) {
        this.filtered.forEach((file) => {
          this.$set(this.selected, file.md5, true)
        })
        this.elCheckAll.checked = true
      } else {
        this.selected = {}
        this.elCheckAll.checked = false
      }
      this.emitSelected()
    },
    onSelectRow(item) {
      let file = item.file
      if (this.selected[file.md5])
        Vue.delete(this.selected, file.md5) // delete was not reactive
      else
        this.$set(this.selected, file.md5, true) // without set, it's not reactive

      this.emitSelected()
    },
    emitSelected() {
      this.$emit('selected', this.filtered.filter((file) => this.selected[file.md5]))
    },
    updateSelectAllFlag(elementCount) {
      this.elCheckAll.indeterminate = elementCount > 0 && elementCount < this.filtered.length;
      this.elCheckAll.checked = elementCount >= this.filtered.length;
      this.selectAll = elementCount >= this.filtered.length;
    }

  }
}
</script>

<style scoped>

</style>